import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Tag, Rocket, Heart, UserPlus, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  description: string;
  onSwitchMode?: (mode: 'signin' | 'signup') => void;
}

const AuthBenefits = ({ onSwitchMode }: { onSwitchMode?: (mode: 'signin' | 'signup') => void }) => {
  const benefits = [
    {
      title: "Create Account",
      icon: <UserPlus className="h-5 w-5 text-white" />,
      mode: 'signup' as const
    },
    {
      title: "Sign in", 
      icon: <LogIn className="h-5 w-5 text-white" />,
      mode: 'signin' as const
    }
  ];

  const handleBenefitClick = (mode: 'signin' | 'signup', title: string) => {
    console.log(`[AuthBenefits] Clicked on ${title}, switching to ${mode} mode`);
    if (onSwitchMode) {
      onSwitchMode(mode);
    }
  };

  return (
    <div className="flex flex-col justify-center h-full px-8 lg:px-12 py-8 space-y-8">
      <div className="space-y-3">
        <div className="flex items-center">
          <img 
            src="/images/finalPiece_logo.png" 
            alt="FinalPiece AI" 
            className="h-12 w-auto object-contain drop-shadow-md dark:hidden"
          />
          <img 
            src="/images/finalPiece_logo_white.png" 
            alt="FinalPiece AI" 
            className="h-12 w-auto object-contain drop-shadow-md hidden dark:block"
          />
        </div>
      </div>

      <div className="space-y-4">
        {benefits.map((benefit, index) => (
          <motion.div 
            key={benefit.title}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 * index, duration: 0.6, ease: "easeOut" }}
          >
            <Button
              variant="ghost"
              onClick={() => handleBenefitClick(benefit.mode, benefit.title)}
              className="group relative w-full p-4 h-auto bg-transparent border-none hover:bg-white/10 transition-all duration-300 rounded-2xl"
            >
              {/* Enhanced glassmorphism background */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-white/5 to-transparent dark:from-slate-700/20 dark:via-slate-800/15 dark:to-transparent rounded-2xl backdrop-blur-sm border border-white/20 dark:border-slate-600/30 opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
              
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-purple-400/10 dark:from-blue-400/20 dark:to-purple-400/20 rounded-2xl filter blur-xl opacity-0 group-hover:opacity-60 transition-all duration-500"></div>
              
              <div className="relative z-10 flex gap-4 items-center w-full group-hover:transform group-hover:scale-[1.02] transition-all duration-300">
                <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-white/20 to-white/10 dark:from-slate-600/40 dark:to-slate-700/30 group-hover:from-white/30 group-hover:to-white/20 dark:group-hover:from-slate-500/50 dark:group-hover:to-slate-600/40 transition-all duration-300 backdrop-blur-sm border border-white/20 dark:border-slate-500/30 shadow-lg">
                  <div className="relative">
                    {benefit.icon}
                    {/* Icon glow effect */}
                    <div className="absolute inset-0 bg-white/20 dark:bg-blue-400/30 rounded-lg filter blur-md opacity-0 group-hover:opacity-70 transition-all duration-300"></div>
                  </div>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-lg text-white group-hover:text-blue-100 dark:group-hover:text-blue-200 transition-colors duration-300 drop-shadow-sm">
                    {benefit.title}
                  </h3>
                  {/* Animated underline */}
                  <div className="h-0.5 bg-gradient-to-r from-blue-300/60 to-purple-300/60 dark:from-blue-400/80 dark:to-purple-400/80 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left mt-1"></div>
                </div>
                
                {/* Subtle arrow indicator */}
                <div className="opacity-0 group-hover:opacity-60 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                  <ArrowRight className="h-4 w-4 text-white/70" />
                </div>
              </div>
            </Button>
          </motion.div>
        ))}
      </div>

      <div className="pt-6 mt-auto">
        {/* Enhanced footer with glassmorphism */}
        <div className="relative p-4 rounded-xl bg-gradient-to-r from-white/10 via-white/5 to-transparent dark:from-slate-700/20 dark:via-slate-800/10 dark:to-transparent backdrop-blur-sm border border-white/20 dark:border-slate-600/20">
          <p className="text-xs text-white/80 dark:text-white/90 max-w-md text-center">
            By signing up, you agree to our{' '}
            <a href="https://www.finalpiece.ai/terms" className="underline hover:text-blue-200 transition-colors" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="https://www.finalpiece.ai/privacy" className="underline hover:text-blue-200 transition-colors" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const AuthLayout = ({ children, title, description, onSwitchMode }: AuthLayoutProps) => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-500 via-purple-500/80 to-accent dark:from-slate-900 dark:via-purple-900/90 dark:to-blue-900/80 relative overflow-hidden">
      {/* Enhanced dark mode background effects */}
      <div className="absolute inset-0 dark:bg-gradient-to-br dark:from-slate-900/95 dark:via-purple-900/85 dark:to-blue-900/90"></div>
      
      {/* Dark mode floating orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 dark:bg-gradient-to-r dark:from-blue-500/20 dark:to-purple-500/20 rounded-full filter blur-3xl animate-pulse dark:block hidden"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 dark:bg-gradient-to-r dark:from-purple-500/15 dark:to-pink-500/15 rounded-full filter blur-3xl animate-pulse delay-1000 dark:block hidden"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 dark:bg-gradient-to-r dark:from-cyan-400/10 dark:to-blue-400/10 rounded-full filter blur-2xl animate-float dark:block hidden"></div>

      {/* Mobile layout - scrollable with proper mobile design system */}
      <div className="lg:hidden relative z-10 min-h-screen">
        <motion.div 
          className="w-full min-h-screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Mobile scrollable container with proper padding */}
          <div className="w-full min-h-screen overflow-y-auto">
            {/* Enhanced mobile form container with mobile design system */}
            <div className="w-full min-h-screen relative">
              {/* Enhanced dark mode mobile background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent dark:from-slate-800/40 dark:via-slate-900/30 dark:to-slate-950/20"></div>
              
              {/* Enhanced floating elements for dark mode */}
              <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-400/10 to-purple-400/10 dark:from-blue-400/25 dark:to-purple-400/25 rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-gradient-to-r from-purple-400/10 to-pink-400/10 dark:from-purple-400/20 dark:to-pink-400/20 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
              
              {/* Enhanced glass container with mobile design system padding */}
              <div className="relative w-full min-h-screen backdrop-blur-2xl bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-slate-800/70 dark:via-slate-900/60 dark:to-slate-950/50 border border-white/30 dark:border-slate-600/40 shadow-2xl dark:shadow-purple-500/10">
                {/* Mobile design system: outer padding p-3 (12px) */}
                <div className="p-3 min-h-screen flex flex-col justify-center">
                  <div className="w-full max-w-md mx-auto">
                    {/* Mobile design system: inner spacing space-y-2 (8px) */}
                    <div className="space-y-2">
                      <div className="text-center mb-6">
                        <div className="flex justify-center mb-4">
                          <div className="relative">
                            <img 
                              src="/images/logos/finalPiece_icon.webp" 
                              alt="Logo" 
                              className="w-16 h-16 object-contain relative z-10" 
                              data-test-id="auth-logo"
                            />
                            {/* Enhanced logo glow for dark mode */}
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/30 to-purple-400/30 dark:from-blue-400/50 dark:to-purple-400/50 rounded-full filter blur-lg"></div>
                          </div>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{title}</h1>
                        <p className="text-gray-600 dark:text-gray-300">
                          {description}
                        </p>
                      </div>
                      
                      {children}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Desktop layout - centered with max width */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-4 relative z-10">
        <motion.div 
          className="container mx-auto max-w-5xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-row items-stretch shadow-2xl dark:shadow-purple-500/20 rounded-2xl overflow-hidden">
            {/* Left column with benefits - only on desktop */}
            <div className="w-5/12 bg-gradient-to-br from-blue-500 via-purple-500 to-accent dark:from-blue-800 dark:via-purple-900 dark:to-accent/80 relative overflow-hidden">
              {/* Animated background patterns */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 dark:bg-white/10 rounded-full filter blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/5 dark:bg-black/15 rounded-full filter blur-3xl"></div>
              
              <AuthBenefits onSwitchMode={onSwitchMode} />
            </div>

            {/* Dramatically enhanced desktop form container for dark mode */}
            <div className="w-7/12 relative overflow-hidden">
              {/* Multi-layered enhanced background for dark mode */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-white/70 to-white/50 dark:from-slate-800/95 dark:via-slate-900/85 dark:to-slate-950/75"></div>
              
              {/* Enhanced floating orbs with stronger dark mode presence */}
              <div className="absolute top-1/4 left-1/3 w-32 h-32 bg-gradient-to-r from-blue-300/20 to-purple-300/20 dark:from-blue-400/40 dark:to-purple-400/40 rounded-full filter blur-2xl animate-float"></div>
              <div className="absolute bottom-1/3 right-1/4 w-24 h-24 bg-gradient-to-r from-purple-300/20 to-pink-300/20 dark:from-purple-400/35 dark:to-pink-400/35 rounded-full filter blur-2xl animate-float delay-1000"></div>
              <div className="absolute top-1/2 right-1/3 w-20 h-20 bg-gradient-to-r from-cyan-300/15 to-blue-300/15 dark:from-cyan-400/30 dark:to-blue-400/30 rounded-full filter blur-xl animate-float delay-500"></div>
              
              {/* Enhanced grid pattern with better dark mode visibility */}
              <div className="absolute inset-0 opacity-5 dark:opacity-15"
                style={{
                  backgroundImage: `radial-gradient(circle at 1px 1px, rgba(139, 92, 246, 0.3) 1px, transparent 0)`,
                  backgroundSize: '20px 20px'
                }}
              ></div>
              
              {/* Main content container with dramatically enhanced glass effect for dark mode */}
              <div className="relative w-full h-full backdrop-blur-3xl p-8 flex items-center justify-center border border-white/40 dark:border-slate-600/50 shadow-inner dark:shadow-purple-500/20">
                {/* Enhanced inner glow effect for dark mode */}
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent dark:via-slate-700/20 pointer-events-none"></div>
                
                {/* Additional dark mode ambient lighting */}
                <div className="absolute inset-0 dark:bg-gradient-to-tr dark:from-blue-500/5 dark:via-transparent dark:to-purple-500/5 pointer-events-none"></div>
                
                <div className="relative w-full max-w-md">
                  <div className="text-center mb-6">
                    <div className="flex justify-center mb-4">
                      <div className="relative">
                        <img 
                          src="/images/logos/finalPiece_icon.webp" 
                          alt="Logo" 
                          className="w-16 h-16 object-contain relative z-10" 
                          data-test-id="auth-logo"
                        />
                        {/* Dramatically enhanced glow for dark mode */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 dark:from-blue-400/60 dark:to-purple-400/60 rounded-full filter blur-lg"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 dark:from-cyan-400/30 dark:to-blue-400/30 rounded-full filter blur-2xl"></div>
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent mb-2">{title}</h1>
                    <p className="text-gray-600 dark:text-gray-300">
                      {description}
                    </p>
                  </div>
                  
                  {children}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthLayout;
